import { isPlEligible, type PlPoolUserShape } from "../users/plPool";

// ---------------------------------------------------------------------------
// ROLLE-1 «Die Doppelrolle» — welche Bereiche hat ein Mensch auf seiner
// Startseite?
// ---------------------------------------------------------------------------
// Anlass (Daniel 2026-08-18): «Warum hat Sandro keine Werkbank? Ich habe schon
// 100x definiert, dass er CEO und Projektleiter ist.»
//
// Bis hierher war die Startseite ein `switch (user.role)` — ein Mensch, ein
// Wert, ein Dashboard. Das Datenmodell konnte die Doppelrolle nie ausdruecken,
// also endete Sandro (`role='admin'`) beim CEO-Cockpit und sah die Werkbank
// nie. Gemessen fuehrt er dabei mit Abstand die meisten Vorgaenge im Haus:
// 122 Projekte, davon 93 in den offenen Status, die die Werkbank zeigt.
//
// Diese Datei ersetzt die Verzweigung durch eine LISTE. Ein Bereich ist der
// heutige Zustand; zwei Bereiche sind ein Umschalter. Nichts wird ersetzt —
// die Rolle bringt ihren Bereich mit, der PL-Hut kommt DAZU.
//
// Pure TS. Kein DOM, kein React, keine DB.
//
// ---------------------------------------------------------------------------
// Warum kein `users.roles: string[]`
// ---------------------------------------------------------------------------
// Die Frage «traegt dieser Mensch den PL-Hut» ist bereits beantwortet:
// `isPlEligible` (shared/users/plPool.ts, Phase 5.59) ist die abgemachte
// Single Source und wird von Cron, Repo, Owner-Picker und der Leads-Kachel
// gelesen. Ein zweiter Traeger daneben waere genau die Doppelquelle, die
// Prinzip 1 verbietet — und ein Rollen-Array haette Berechtigungen angefasst,
// was hier niemand bestellt hat.
//
// Diese Datei ruft `isPlEligible` deshalb SELBST auf, statt ein Ja/Nein
// entgegenzunehmen. Das ist der Unterschied zwischen einer Regel und einer
// Meinung: superadmin und buchhaltung koennen hier strukturell keinen PL-Hut
// bekommen, auch wenn ein Aufrufer das behaupten wollte.

export const HOME_BEREICHE = [
  /** CEO-Cockpit (`DashboardCEO`) — Firmenzahlen, Pipeline, Heute-Strom. */
  "fuehrung",
  /** Die Werkbank (`DashboardPL`) — die eigenen Projekte und Anfragen. */
  "projektleitung",
  /** Buchhaltung (`DashboardBuchhaltung`). */
  "buchhaltung",
  /** Superadmin-Kommandostand (`DashboardSuperadmin`). */
  "kommandostand",
] as const;

export type HomeBereich = (typeof HOME_BEREICHE)[number];

/** Beschriftung des Umschalters. Nur sichtbar, wo es mehr als einen gibt. */
export const HOME_BEREICH_LABELS: Record<HomeBereich, string> = {
  fuehrung: "Geschäftsführung",
  projektleitung: "Projektleitung",
  buchhaltung: "Buchhaltung",
  kommandostand: "Kommandostand",
};

/**
 * Der Bereich, den die Rolle allein schon mitbringt — die Uebersetzung des
 * alten `switch (user.role)`, Fall fuer Fall. `admin` ist die Fuehrung, und
 * `admin` ist auch der `default`-Zweig von frueher: eine unbekannte Rolle
 * landete dort schon immer.
 */
function bereichDerRolle(role: PlPoolUserShape["role"]): HomeBereich {
  switch (role) {
    case "superadmin":
      return "kommandostand";
    case "pl":
      return "projektleitung";
    case "buchhaltung":
      return "buchhaltung";
    case "admin":
      return "fuehrung";
  }
}

/**
 * Die Bereiche eines Menschen, in der Reihenfolge, in der sie angeboten
 * werden. Der ERSTE ist der, auf dem er landet — er bleibt immer der Bereich
 * seiner Rolle, damit die Doppelrolle nichts wegnimmt (Sandro landet weiter
 * auf seinem CEO-Cockpit).
 *
 * `tier` ist bewusst `null`-bar und bedeutet hier zweierlei, was fuer das
 * Ergebnis dasselbe heisst: «kein Tier gesetzt» ODER «noch nicht geladen».
 * Beide Male gibt es keinen PL-Hut. Das ist fail-closed und Absicht: der
 * Bereich taucht einen Wimpernschlag spaeter auf, statt einmal zu frueh bei
 * jemandem, der ihn nie haben darf.
 *
 * Der PL-Hut wird nur ANGEHAENGT, wenn die Rolle nicht ohnehin schon in der
 * Werkbank steht — ein `pl` bekommt seinen Arbeitsplatz nicht doppelt. Und
 * weil `isPlEligible` hier drin laeuft, kann der Hut nur auf `pl` und `admin`
 * fallen; die Negativ-Kontrolle in `tests/rolle1-home-bereiche.test.ts` faehrt
 * genau das ueber den ganzen Rollen-/Tier-Raum ab.
 */
export function homeBereiche(user: PlPoolUserShape): readonly HomeBereich[] {
  const eigener = bereichDerRolle(user.role);
  if (eigener === "projektleitung") return [eigener];
  if (!isPlEligible(user)) return [eigener];
  return [eigener, "projektleitung"];
}

/** Lesbare Abfrage statt `.includes` an sieben Stellen. */
export function hatBereich(
  bereiche: readonly HomeBereich[],
  gesucht: HomeBereich,
): boolean {
  return bereiche.includes(gesucht);
}
